import { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useTranslation } from 'react-i18next';

type ScrollFooterProps = {
  dependencies?: unknown[];
};

export const ScrollFooter = ({ dependencies = [] }: ScrollFooterProps) => {
  const { t } = useTranslation();
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollHeightRef = useRef(0);

  useEffect(() => {
    const checkScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;

      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

      setShowScrollIndicator(isScrollable && !isAtBottom);
      scrollHeightRef.current = scrollHeight;
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    window.addEventListener('scroll', checkScroll);

    // Use MutationObserver to detect DOM changes (IE11 compatible)
    const mutationObserver = new MutationObserver(() => {
      const newScrollHeight = document.documentElement.scrollHeight;
      if (newScrollHeight !== scrollHeightRef.current) {
        checkScroll();
      }
    });

    // Observe document body for DOM changes (when children content changes)
    if (document.body) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    return () => {
      window.removeEventListener('resize', checkScroll);
      window.removeEventListener('scroll', checkScroll);
      mutationObserver.disconnect();
    };
  }, [...dependencies]);

  if (!showScrollIndicator) return null;

  return (
    <div className="bg-primary-100 flex justify-center py-2 w-full">
      <p>{t('common.layout.scroll_down')}</p>
      <Icon icon="mdi:arrow-down" className="animate-bounce w-6 h-6" />
    </div>
  );
};
